//-- Copyright (c) 2021 TMT Insights Group, LLC
import {
  ColumnConfig,
  CustomCycleDate,
  DashboardConfig,
  DataTableConfig,
  Profiles,
  Sorting,
  StatusCard,
  TitleSearchMode
} from '@/__configs/config_types';
import { SelectedTitles } from '@/components/IngestManagement/FileIngest/TitleAssociation';
import { SelectedTitlesV2 } from '@/components/IngestManagement/FileIngest/TitleAssociationV2/TitleAssociationV2';
import { InventoryMetadataChip } from '@/components/TitleDetail/TitleDetailInventory/MetadataTag';
import { FileFormat, FileTypeGrouping, LoadingStatuses, TitleTypes } from '@/constants/constants';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import Form from '@rjsf/core';
import { RJSFSchema, RJSFValidationError } from '@rjsf/utils';
import React from 'react';

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export interface PolarisApiResponse<T = any> {
  success: boolean;
  message: string;
  data: T;
  pagination?: Pagination;
}

export interface JobState {
  data: JobDetail;
  relatedFiles: RelatedFiles;
  pagination?: Pagination;
  status: LoadingStatuses;
}

export interface RelatedFiles {
  data: JobDetailInventoryFile[];
  status: LoadingStatuses;
}
export interface LayoutState {
  isNavBarVisible: boolean;
  schema: {
    showDialog: boolean;
    errors: RJSFValidationError[];
  };
}

export interface OrderDetail {
  billable_customer: string;
  created_at: string;
  created_by: string;
  description: string;
  hid: string;
  id: number;
  jobs: OrderJob[];
  managed_by: string;
  managed_by_user: UserProfile;
  po_number: string;
  status: string;
  total_jobs: number;
  order_type: string;
  earliest_due_date_by_jobs: string;
}

export interface ProfileState {
  workflows: { [key: string]: Workflow };
  profiles: { [key: string]: CustomerProfile[] };
  allProfiles: CustomerProfile[];
  deletedProfiles: CustomerProfile[];
  showDeletedProfilesInJobsFilter: boolean;
}

export type FileOriginType = 'no_source' | 'unknown' | 'aws_s3' | 'aspera_node' | 'qumulo' | 'gcs' | 'signiant';

export interface FileSource {
  id: number;
  connectors: FileConnector[];
  name: string;
  slug: string;
  order: number;
  origin_type: FileOriginType;
  is_hidden: boolean;
  is_read_only?: boolean;
}

export interface Folder {
  success: boolean;
  items: FileManagerInventoryFile[];
  paging_info: Pagination;
}

export interface FileManagerState {
  data: JobDetailInventoryFile[][];
  currentPath: string[];
  currentPathString: string;
  nextPages: string[];
  fileSources: FileSource[];
  currentDepth: number;
  connectors: Record<string, Connector>;
  searchParams: {
    prefix_search?: string;
  };
  locations: Location[];
  locationPointer: number;
  hasLoadedSourcesData: boolean;
  pagination: Partial<Pagination>;
  status: LoadingStatuses;
  folderLoading: boolean;
  isTransferQueueDrawerOpen: boolean;
  lastOpenedPendingAssetLoaction: null | string[];
}

export interface FileQueueState {
  items: FileManagerInventoryFile[];
}

export interface OrderPlacementState {
  data: any;
  inventorySelections: InventoryFileEntity[];
  versionSelections: (TitleEntity | TitleEntityV2)[];
  orderDetails: OrderDetail;
  outputVersions: TitleEntity[];
  squashedOutputVersions: {
    [key: number]: number[];
  };
  loadingOutputVersions: boolean;
  searchValue?: string;
  searchResults: {
    data: TitleEntity[];
    loading: boolean;
  };
  billableCustomers: {
    data: Customer[];
    loading: boolean;
  };
  step: number;
  outputVersionOptions: { [key: string]: TitleEntity[] };
  versionKeys: Record<string, TitleEntity | TitleEntityV2>;
  profileData: Profiles;
  primaryVersionIds?: number[];
  newOrderId: string | null;
  previewFile: InventoryFileEntity | null;
  activeVersion: TitleEntity | null;
  validationErrors: any;
  titleInput: Partial<TitleInput>;
  status: LoadingStatuses;
  openCancelationDialog: boolean;
  formIsIncomplete: boolean;
  formIsDraft: boolean;
  isResumedDraft: boolean;
  draftState: any;
  selectedVersionId: number | null;
  filters: any;
  schemaFormRefs: Map<string, React.RefObject<Form>>;
  selectedHierarchy: string;
  timeZonedDate?: string;
  toggledTitleSearchMode: TitleSearchMode;
}

export enum SchemaFormRefSource {
  PROFILE_DETAIL_INFO = 'ProfileDetailInfo',
  PROFILE_SPEC_INFO = 'ProfileSpecInfo',
  JOB_PROFILE_DETAILS_FORM = 'OPJobProfileDetailsForm'
}

export interface TitleInput {
  deliver_to_mpx: boolean;
  edit_type: string;
  output_version_id: string;
}

export interface Location {
  path: string;
  savedState: SavedState;
}

export interface SavedState {
  data: JobDetailInventoryFile[][];
  currentDepth: number;
  currentPath?: string[];
  pagination: Partial<Pagination>;
  nextPages: string[];
}
export interface FileConnector {
  allows_uploads: boolean;
  connector_type: FileOriginType;
}

export interface Connector {
  is_read_only?: boolean;
  aws_s3?: boolean;
  aspera_node?: {
    allows_upload?: boolean;
  };
  allows_upload?: boolean;
  connector_type?: FileOriginType;
}

/* ***********************************
  name: AuthState
  purpose: AuthState is the currently a property stored in the Redux state.
    Contains authorization information for logging into app
  *********************************** */
export type AuthState = {
  initFinished: boolean;
  email: string | null;
  status: LoadingStatuses;
  user: UserProfile;
  preferences: Partial<UserPreferences>;
  permissions: string[];
  routes: string[];
  appVersion: AppVersion | null;
  site: SiteObject | null;
  timeZone: string | null;
};

export const TITLE_HISTORY_DEFAULT_KEY = '__default__';

export type TitleHistoryBuckets = Record<string, HistoricTitleEntity[]>;

export type FiltersLayout = {
  name: string;
  is_selected?: boolean;
  filters: FilterLayoutsFilter[];
};

export interface FilterLayoutsFilter {
  id: string;
  is_quick: boolean;
  is_hidden: boolean;
  label: string;
}

export type UserPreferences = Preferences & {
  [key in keyof DataTableConfig]:
    | {
        columns: TableColumn[];
        saved_searches: Search[];
        saved_filter_layouts?: FiltersLayout[];
        history?: Record<ModuleType, TitleHistoryBuckets>;
      }
    | any;
};

export type HistoricTitleEntity = {
  id: number;
  name: string;
  hierarchy?: string;
};

export interface Search {
  name: string;
  filters: {
    status?: string[];
    stage?: string[];
    due_date?: {
      due_date__lte: string;
      due_date__gte: string;
    };
    created_at?: {
      created_at__lte: string;
      created_at__gte: string;
    };
    assigned_users_filter?: Partial<UserProfile>[];
    type?: string[];
    show_not_ready?: boolean;
    search?: string;
  };
  sorting: Sorting;
}

/* ***********************************
  name: BulkSearch
  purpose: BulkSearch is the currently a property stored in the Redux state
    Contains information relating to a bulk searches in the app.
*********************************** */
export interface BulkSearchState {
  draftOrderId: null | number;
  status: LoadingStatuses;
  file: BulkUploadedFile;
  autoSubmit: boolean;
  modalOrderId: null | string | number;
  titles: TitleEntity[];
  errors: Array<{
    id: string;
    error: string;
  }>;
}

export interface BulkUploadedFile {
  name: string;
}

export interface BulkSearchResponse {
  success: boolean;
  message: string;
  data: TitleEntity[];
}

/* ***********************************
  name: ConstantsState
  purpose: ConstantsState is the currently a property stored in the Redux state.
    Contains constant variables used throughout the app.
*********************************** */
export interface ConstantsState {
  orders: {
    status?: LoadingStatuses;
    customers?: Customers;
    customerIDNames: Customers;
    profileCustomers?: Customers;
    billableCustomers?: Customer[];
    nonBillableCustomers?: Customer[];
    statuses: { [key: string]: string };
    taskItemStatuses: { [key: string]: string };
    categories?: { [key: string]: string };
  };
  jobs: {
    statuses: { [key: string]: string };
    stages: { [key: string]: string };
    types: { [key: string]: string };
    jobWorkflows: { [key: string]: string };
    job_priority_levels?: { [key: string]: string };
  };
  tasks: {
    statuses: { [key: string]: string };
    types: { [key: string]: string };
    manualTaskNames: { [key: string]: string };
    taskTypeGroups: { [key: string]: string };
    networkCodes: any;
    qc_task_types?: { [key: string]: string };
    task_qc_screening_results?: { [key: string]: string };
  };
  pendingAssets: {
    statuses: { [key: string]: string };
    systemStatuses: { [key: string]: string };
    humanStatuses: { [key: string]: string };
    assignable_task_groups: { id: number; group: string }[];
    componentTypes?: { [key: string]: string };
    contentTypes?: { [key: string]: string };
  };
  titles: {
    types: typeof TitleTypes | { [key: string]: string };
    entityTypeLabels?: { [key: string]: string };
    hierarchies?: { [key: string]: string };
    entity_status?: { [key: string]: string };
  };
  fileIngest: {
    statuses: { [key: string]: string };
    stages: { [key: string]: string };
    file_types: { [key: string]: string };
  };
  transferQueue: {
    statuses?: { [key: string]: string };
  };
  userEmails: any;
  vfs_playback_base_url: string;
  vfsTaskTypes: { [key: string]: string };
  initFinished: boolean;
  status: LoadingStatuses;
  languages?: LanguageConstants;
  media_resolutions: MediaResolution[];
  commonConstants?: CommonConstants;
  api_extras?: any;
  bulkActions?: BulkActions;
  exceptions: {
    priorities: { [key: string]: string };
    referenceTypes: { [key: string]: string };
    statuses: { [key: string]: string };
    subtypes: { [key: string]: string };
    types: { [key: string]: string };
    severities: { [key: string]: string };
  };
  avails: {
    localeTerritory: { [key: string]: string };
    localeDialect: { [key: string]: string };
    license: { [key: string]: string };
    availedPlatform: { [key: string]: string };
    availedLanguage: { [key: string]: string };
    scheduleStatus: { [key: string]: string };
    platformIds: { [key: string]: string };
  };
  packaging: {
    cutTypeLabels: { [key: string]: string };
    accurate_key?: string;
  };
  fileTypeGroupingsFromApi?: FileTypeGrouping;
  territoryGroupings?: { [key: string]: { label: string; value: string }[] };
  geoTerritoryGroupings?: { [key: string]: { label: string; value: string }[] };
  timezoneTerritoryGroupings?: { [key: string]: string };
}

export interface CommonConstants {
  fileTypeGrouping: FileTypeGrouping;
}

export interface LanguageObject {
  code: string;
  label: string;
  is_short_list: boolean;
}

export interface LanguageConstants {
  audio: LanguageObject[];
  base: LanguageObject[];
  subtitles: LanguageObject[];
}

export type ConstantsResponse = PolarisApiResponse<Constants>;
export type FocusConstantsResponse = PolarisApiResponse<FocusConstants>;
export interface LanguageResponse {
  success: boolean;
  message: string;
  data: LanguageConstants;
}

export type StatusStageObject = {
  status: string;
  stage?: string;
};

export type BulkActions = {
  [key in keyof DataTableConfig]: {
    [key: string]: {
      action_type: string;
      allowed_statuses: StatusStageObject[];
      icon?: string;
      ident: string;
      intermediate_status: string;
      version?: number;
      action_schema: ActionSchema;
    };
  };
};

type ActionSchema = {
  properties: {
    [key: string]: {
      type: string;
      title: string;
      format: string;
    };
  };
  title: string;
  type: string;
};

export interface FocusConstants {
  focus_avails_availed_platform: [string, string, number][];
  focus_avails_delivery_mode: string[][];
  focus_avails_license: string[][];
  focus_avails_license_supplemental: string[][];
  focus_avails_territory: string[][];
  focus_avails_video_resolution_type: string[][];
}

export interface Constants {
  accurate_key?: string; // license key for Accurate Player
  file_ingest_status: string[][];
  file_type_groupings?: FileTypeGrouping;
  focus_avails_availed_platform: string[][];
  job_asset_component_types: string[][];
  order_job_asset_types: string[][];
  order_categories: string[][];
  job_asset_human_status: string[][];
  job_asset_status: string[][];
  job_asset_system_status: string[][];
  job_stages: string[][];
  job_status: string[][];
  job_types: string[][];
  job_workflows: string[][];
  grunt_task_status: string[][];
  order_status: string[][];
  task_names: string[][];
  task_type_groups: string[][];
  manual_task_names: string[][];
  task_status: string[][];
  task_qc_screening_results?: string[][];
  title_types?: string[][];
  entity_type_labels?: string[][];
  vfs_playback_base_url: string | null;
  task_item_status?: string[][];
  assignable_task_groups?: [number, string][];
  media_resolutions: MediaResolution[];
  commonConstants?: CommonConstants;
  network_codes: string[];
  ingest_manager_status: string[][];
  ingest_manager_stage: string[][];
  ingest_manager_file_types: string[][];
  vfs_task_types: string[][];
  bulk_actions: BulkActions;
  monitoring_event_priority: string[][];
  monitoring_event_reference_type: string[][];
  monitoring_event_status: string[][];
  monitoring_event_subtype: string[][];
  monitoring_event_type: string[][];
  monitoring_event_severity: string[][];
  cut_type_labels?: string[][];
  avail_territory_groupings?: Record<string, TerritoryConstant[]>;
  avail_territory_timezone_groupings?: Record<string, string>;
  avail_geo_territory_groupings?: Record<string, TerritoryConstant[]>;
  job_priority_levels?: string[];
  qc_task_types?: string[];
  hierarchies?: string[];
  entity_status?: string[][];
}

/* ***********************************
  name: DashboardState
  purpose: DashboardState is the currently a property stored in the Redux state.
    Contains constant dashboard data used throughout the app.
*********************************** */
export interface DashboardState {
  data: DashboardData;
  ogData: OGDashboardData;
  dailyReportData: DailyReportResponse;
  generatedReportData: DailyReportResponse;
  missingComponents?: any[];
  currentCycle: string | CustomCycleDate;
  status: LoadingStatuses;
  dailyReportStatus: LoadingStatuses;
  generatedReportStatus: LoadingStatuses;
  config: DashboardConfig;
  graphs: {
    status: {
      condensed: boolean;
      editable: boolean;
      saveChanges: boolean;
    };
    recentTasks: {
      all: OrderJobTask[];
      me: OrderJobTask[];
    };
    readOnly: {
      orderBuildType: 'default' | 'custom';
      list: { id: string; checked: boolean; fullRow?: boolean }[];
      order: string[][];
      statusCards: StatusCard[];
    };
    editable: {
      orderBuildType: 'default' | 'custom';
      list: { id: string; checked: boolean; fullRow?: boolean }[];
      order: string[][];
      statusCards: StatusCard[];
    };
  };
}

export interface OGDashboardData {
  jobStats: JobsStatsResponse;
  outstandingAssets: OutstandingAssetsData;
}
export interface DashboardData {
  [key: string]: JobStatsDate | any;
  total?: number;
  tasksProgressMonthlyAvg: [{ task_type_group: 'edit'; data: MonthAndAverage[] }, { task_type_group: 'review'; data: MonthAndAverage[] }];
  totalJobsCompletedByType: TotalJobsCompletedByType;
}

type MonthAndAverage = {
  date: string;
  average_length_in_minutes: number;
};

export interface JobsStatsResponse {
  success: boolean;
  message: string;
  data: JobStats;
  tasks_progress_to_complete_monthly_average_length?: any;
  total_jobs_completed_by_type?: TotalJobsCompletedByType;
}

export interface OutstandingAssetsResponse {
  success: boolean;
  message: string;
  data: OutstandingAssetsData;
}

export interface OutstandingAssetsData {
  [key: string]: OutsandingAsset;
}

export interface OutsandingAsset {
  id: number;
  create_date: string;
  unallocated_files: number;
  oustanding_material: number;
  subs_not_complete: number;
}

export interface TotalJobsCompletedByType {
  [key: string]: JobTypeData[];
}

export interface JobTypeData {
  count: number;
  job_type: string;
  job_type_display: string;
}
export interface JobStats {
  [key: string]: JobStatsDate | number;
  on_time_complete: number;
  total: number;
}

export interface JobStatsDate {
  ai_processing: JobStatsData;
  awaiting_assets: JobStatsData;
  completed: JobStatsData;
  edit: JobStatsData;
  edit_ingest: JobStatsData;
  ingest: JobStatsData;
  on_time_complete: number;
  packaging: JobStatsData;
  prep_for_edit: JobStatsData;
  prep_for_review: JobStatsData;
  render: JobStatsData;
  review: JobStatsData;
  send: JobStatsData;
  submit: JobStatsData;
  total: number;
}

export interface JobStatsData {
  stage: string;
  total: number;
}

/* ***********************************
  name: DataTableState
  purpose: Contains data table data used throughout the app.
*********************************** */
export interface DataTableState extends DataTableConfig {
  currentTable: keyof DataTableConfig;
  currentModule: ModuleType | null;
}

export type ModuleType = 'orderPlacement' | 'packaging';

/* ***********************************
  name: CustomersResponse
  purpose: CustomersResponse is the response from customers endpoint
  used in:
    - GET /v1/common/constants/ - Constants Get API
*********************************** */
export interface CustomerResponse {
  success: boolean;
  message: string;
  data: Customer[];
}

export interface Customer {
  id: number;
  allowed_groups: number[];
  created_at: string;
  updated_at: string;
  deleted_at: string;
  name: string;
  description: string;
  is_billable_customer: boolean;
  is_distributor: boolean;
  contact_name: string | null;
  street_address: string | null;
  street_address2: string | null;
  city: string | null;
  state: string | null;
  zipcode: string | null;
  phone_number: string | null;
  email: string | null;
  external_id: number | null;
  source: string | null;
  created_by: number;
  updated_by: number;
}

export interface Customers {
  [key: string]: string;
}

export interface TaskMilestone {
  [key: string]: any;
  completed_at: string | null;
  name: string;
  name_pretty: string;
  tasks?: OrderJobTask[];
}

export type VersionGroupedByParent = { [titleId: string]: TitleEntity | TitleEntityV2 | VersionGroupedByParent };

export interface TitleMetadata {
  language: string;
  [key: string]: any;
}

export interface Meta {
  [key: string]: any;
}

export interface VersionKeys {
  [key: string]: TitleEntity | TitleEntityV2;
}

/* ***********************************
  name: TitleEntity
  purpose: TitleEntity is the type of the object in the data's array of TitleEntityList
  used in:
    - GET /v1/inventory/titles/search/ - Inventory Title Post Search API
    - POST /v1/inventory/titles/{id}/versions/ - Inventory Title Versions Post Search API
*********************************** */
export interface TitleResponse {
  success: boolean;
  message: string;
  data: TitleEntity;
}

export interface TitleSearchResponse {
  success: boolean;
  message: string;
  data: TitleEntity[];
}

export interface TitleVersionsResponse {
  success: boolean;
  message: string;
  data: TitleEntity[];
  pagination: Pagination;
}

// TODO(TMT-6046): should be removed in favour of TitleEntityV2
export interface TitleEntity {
  [key: string]: any;
  alternate_name: string;
  id: number;
  hid: string;
  entity_type: string;
  meta?: Meta;
  thumbnail_link: string | null;
  name: string;
  program_type?: string;
  children: TitleEntity[];
  runtime: number;
  release_year: string;
  rally_id?: string;
  alpha_id?: string;
  content_studio?: string;
  content_provider?: string;
  // At the moment of writing this comment, the `lineage` type is not standardized across the clients.
  //  For example, for Crunchyroll it's a `number[]`, and for A&E it's a `string[]`
  lineage: (number | string)[];
  lineage_meta: LineageMeta;
  external_id?: string | null;
  metadata_schema?: RJSFSchema;
  metadatas?: TitleMetadata[];
  // client-specific metadata
  program_id: string;
  piece_id: string | null;
  display_id: string | null;
  friendly_id: string | null;
  parent?: number;
  sequence_order: string | number; // for some clients `sequence_order` is string (like Crunchyroll) and for some it's a number (like A&E)
  og_entity_type?: string; // not sure, most probably some client-specific field. If you know better, please update the comment.
  is_screened?: boolean; // used by PBS in the Packaging app
}

// TODO: the usage of `activeVersion` property should be refactored.
//  ActiveVersion should not be such a mix of different types.
//  It feels like Record<string, never> and Record<'showUnspecified', boolean> should not belong to ActiveVersion type.
//  When `activeVersion` will have proper type the `isActiveVersionAVersion` type guard function can be removed.
export type ActiveVersion = null | Record<string, never> | Record<'showUnspecified', boolean> | TitleEntity | TitleEntityV2;

export const isActiveVersionAVersion = (activeVersion: ActiveVersion): activeVersion is TitleEntity | TitleEntityV2 =>
  activeVersion !== null && Boolean((activeVersion as TitleEntity).id);

export const isShowUnspecified = (activeVersion: ActiveVersion) => Boolean(activeVersion && (activeVersion as any).showUnspecified);

export interface LineageMeta {
  [key: string]: any;
  brand_name?: string | null;
  brand_sequence_order?: number | null;
  episode_sequence_number?: number | null;
  parent_entity_type?: string | null;
  program_name?: string | null;
  program_sequence_order?: number | null;
  season_name?: string | null;
  series_name?: string | null;
  series_number?: number | null;
  series_sequence_order?: number | null;
  season_sequence_order?: number | null;
}

/* ***********************************
  name: JobDetail
  purpose: JobDetail is the data property from the JobDetails endpoint
  used in:
    - POST /v1/core/jobs/{id}/ - Job Detail Post API
*********************************** */
export interface JobDetailResponse {
  success: boolean;
  data: JobDetail;
  constants?: ConstantsState;
}

export interface JobDetail {
  additional_info: RJSFSchema;
  accurate_player_link?: string;
  assets: Assets;
  bundle_job_hid?: string | null;
  category: string | null;
  completed_at: string | null;
  completed_by: string;
  // will eventually be replaced
  context?: {
    [key: string]: any;
    metadata?: {
      'audio-track-n'?: {
        lang?: string;
        track?: number;
      }[];
      'subtitle-n'?: {
        lang?: string;
        track?: number;
      }[];
    };
  };
  created_at: string;
  created_by: string | null;
  customer: Required<ConstantsState['orders']>['customers'][number];
  deleted_at: string | null;
  deliverables: Deliverable[];
  delivery_language?: string | null;
  delivery_languages?: string[];
  description: string;
  due_date: string | null;
  embargo_date: string | null;
  embargo_resumed_at: string | null;
  external_system_id: string | number;
  hid: string;
  id: number;
  job_assets?: null;
  job_type: ConstantsState['jobs']['types'][number];
  job_user_inputs?: RJSFSchema;
  order: {
    id: number;
    hid: string;
    description: string;
    managed_by?: UserProfile;
  };
  output_title?: TitleEntity;
  output_version?: TitleEntity;
  output_network?: Record<string, string>;
  origin_job: null | { id: number };
  profile: CustomerProfile | null;
  priority_level: string | null;
  service_meta?: any;
  stage: Extract<keyof ConstantsState['jobs']['stages'], string>;
  status_reason: string | null;
  status: Extract<keyof ConstantsState['jobs']['statuses'], string>;
  task_name: string;
  task_status: Extract<keyof ConstantsState['tasks']['statuses'], string>;
  task_timeline: TaskMilestone[];
  task: OrderJobTask;
  tasks: OrderJobTask[];
  title: TitleEntity;
  updated_at: string;
  version: TitleEntity;
  job_user_input_schema?: RJSFSchema;
  job_user_input_values?: any;
  primary_rally_url?: string;
  material_id?: string;
  managed_by?: string;
}

export interface JobDrafts {
  [key: string]: JobDraft;
}

export interface JobDraft extends Pick<JobDetail, 'customer' | 'due_date' | 'embargo_date' | 'id' | 'profile' | 'version'> {
  instance_inputs: any;
  title_inputs: any;
  order_profile_inputs?: Record<string, any>;
  order_spec_inputs?: Record<string, any>;
}

export interface FormValues {
  jobs: FormJob[];
  [key: string]: any;
}

export interface FormJob extends Partial<JobDetail> {
  title_inputs: {
    [key: string]: any;
  };
  order_profile_inputs?: {
    [key: string]: any;
  };
  profile: CustomerProfile;
  due_date: string;
}

export type Assets = {
  total: number;
  progress: number;
  received: number;
  workflow_components_received?: number;
  workflow_components_total?: number;
  components: AssetComponent[];
  deliverables?: Deliverable[];
};

export type AssetComponent = {
  alternate_hierarchy?: number;
  filename: string;
  label: string;
  proxy_label: string;
  status: string;
  sub_type: string;
  type: AssetType;
  status_reason?: string;
  status_changed_at?: string;
  requested_order?: number;
  version_name?: string;
  version_id?: number;
  version_external_id?: string;
  lang?: string;
  job_asset_id?: number;
  job_asset_hid?: string;
  origin_job_id?: number;
  related_asset_id?: number | number[];
  related_asset_status_reason?: string;
  alternate_to?: string;
  local_id?: string;
  optional?: boolean;
};

export type AssetType = 'video' | 'audio' | 'subtitle' | 'captions' | 'image' | 'other';

export type Deliverable = {
  name: string;
  order: number;
  status: string;
  sub_type: string;
  type: AssetType;
};

/* ***********************************
  name: Order
  purpose: Order is the type of the object in the data's array of OrderList
  used in:
    - GET /v1/inventory/orders/search/ - Orders Post Search API
*********************************** */
export interface Order {
  [key: string]: any;
  jobs: OrderJob[];
  id: number;
  hid: string;
}

export interface OrderJob
  extends Omit<
    JobDetail,
    | 'accurate_player_link'
    | 'completed_by'
    | 'deliverables'
    | 'job_assets'
    | 'job_user_inputs'
    | 'output_title'
    | 'output_version'
    | 'origin_job'
    | 'status_reason'
    | 'task_timeline'
    | 'task'
    | 'tasks'
  > {
  availability_start_date: string;
  availability_end_date: string;
  linear_tx_date: string;
  managed_by: string;
  total_jobs: number;
  updated_by: string;
  stats: {
    total: number;
  };
  message?: any;
  qc_by_date?: string;
  qc_on_prem?: boolean;

  // client-specific properties
  component_deliverable_type?: string;
  component_deliverable_sub_type?: string;

  piece_id?: string;
  piece_number?: string;
}

export interface InventoryFileEntity {
  id: number;
  label: string;
  status: string;
  available: boolean;
  instances: {
    [key: string]: FileInstance;
  };
  name: string;
  media_info: MediaInfo;
  file_type: FileFormat;
  file_size: number;
  version_name: string;
  version_id: number;
  version_external_id: string;
  rally_asset_tags: string[];
  important_tags: { label: string; color: string }[];
  inventory_tags?: InventoryMetadataChip[];
  [key: string]: any;
}

// /api/v1/jobs/{id}/rally_inventory/ - aka related assets tab on job detail
export type JobDetailInventoryFile = Pick<InventoryFileEntity, 'label' | 'name' | 'file_type' | 'status'> & {
  storage_location_name: string;
  last_modified: string;
  size: number;
  source_id: number;
  source_name: string;
};

// /api/v1/vfs/files/glb_ingest_qa_node/ - FILE MANAGER
export interface FileManagerInventoryFile extends InventoryFileEntity {
  origin_type: FileOriginType;
  origin_name: string;
  origin_slug: string;
  name: string;
  ptype: string;
  key: string;
  size: number;
  last_modified: string;
  has_vfs_lock: boolean;
  extra: null | any;
  owner: string;
  storage_class: string;
}

export interface FileInstance {
  name: string;
  size: number;
  lastModified: string;
  status: string;
  storageLocationName: string;
  uri: string;
}

/* ***********************************
  name: Pagination
  purpose: Pagination is the pagination object used for paginating responses
  used in:
    - POST /v1/core/tasks/search/ - Tasks Post List API
*********************************** */
export interface Pagination {
  page?: number;
  page_size: number;
  total: number;
}

export interface ExtraMetaSchema {
  title: string;
  type: string;
  properties: {
    [key: string]: ExtraMetaSchemaProperty;
  };
  readonly?: boolean;
  readOnly?: boolean;
}

export interface ExtraMetaSchemaProperty {
  format: string;
  title: string;
  type: string;
  readOnly?: boolean;
  ui_cols: {
    [key in 'xs' | 'sm' | 'md' | 'lg' | 'xl']: number;
  };
}

export interface OrderJobTask {
  assigned_user: UserProfile | null;
  created_at: string;
  completed_at: string | null;
  extra_meta_schema: ExtraMetaSchema;
  extra_meta?: {
    action_completed?: boolean;
    accurate_result?: string;
    accurate_remarks?: string;
    v_chip_rating?: string;
    task_type?: string;
  };
  hid: string;
  id: number | string;
  is_manual_task: boolean;
  is_skippable_task: boolean;
  is_holdable_task?: boolean;
  is_assets_task?: boolean;
  profile?: string;
  job: {
    assets: Assets;
    availability_start_date: string;
    availability_end_date: string;
    due_date: string | null;
    external_system_id: string | number;
    id: number;
    job_type: Extract<keyof ConstantsState['jobs']['types'], string>;
    priority_level?: string | null;
    stage: Extract<keyof ConstantsState['jobs']['stages'], string>;
    status: Extract<keyof ConstantsState['jobs']['statuses'], string>;
    task_name: string;
    task_status: Extract<keyof ConstantsState['tasks']['statuses'], string>;
    title: TitleEntity;
    tx_date?: string;
    version?: TitleEntity;
    qc_by_date?: string;
    qc_on_prem?: boolean;
    output_network?: Record<string, any>;
  };
  input_data?: {
    rally_asset_name: string;
  };
  name: string;
  pretty_name: string;
  priority: number;
  status: string;
  status_reason: string;
  step: string;
  task_action?: {
    action_url?: string;
    type?: string;
    actions?: any;
    schema_template?: RJSFSchema;
    icon?: string;
  };
  task_action_input?: any;
  updated_at: string;
  resolved_by?: UserProfile;
  resolution_notes?: string;
  unread_comments_count: number;
  platform?: string;
  due_date?: string;
  order_hid?: string;
  target_duration?: string;
}

/* ***********************************
  name: UserProfile
  purpose: UserProfile is the type of the object in the data of ResponsePlatformUserProfile
  used in:
    - GET /v1/auth/login - UserProfile API Get
*********************************** */
export type UserProfileResponse = PolarisApiResponse<UserProfile>;

export type WorkflowType = 'version_based' | 'component_based' | 'bundle';

export type WorkflowsResponse = PolarisApiResponse<Workflow[]>;
export interface Workflow {
  name: string;
  instance_schema: RJSFSchema;
  job_class: string;
  ident: string;
  template_schema: RJSFSchema;
  title_schema: RJSFSchema;
  workflow_type: WorkflowType;
  order_spec_schema?: RJSFSchema;
  order_profile_schema?: RJSFSchema;
  workflow_version?: string;
}

export interface UserProfile {
  accepted_terms?: boolean;
  type?: string;
  id: number;
  username?: string;
  email: string | null;
  preferences?: Preferences | null;
  first_name: string | null;
  last_name: string | null;
  updated_at?: string | null;
  user_permissions?: Permission[];
  groups?: UserGroup[] | null;
  customer_associations?: Customer[];
  color: string | null;
  initials?: string;
  is_active: boolean;
}

/* ***********************************
  name: GroupProfile
  purpose: GroupProfile is the object that contians group information
*********************************** */
export interface GroupProfile {
  id: number;
  group: string;
}

/* ***********************************
  name: LoginResponse
  purpose: LoginResponse is the type of the object in the data of LoginAPIPostResponseLoginAPIPostResponse
  used in:
    - POST /v1/auth/profile - LoginAPI Post
*********************************** */
// export interface LoginResponse {
//   success: boolean;
//   message: string;
//   data: UserProfile;
// }

export type LoginResponse = PolarisApiResponse<
  UserProfile & {
    token: string;
  }
>;

export interface Preferences {
  dark_mode?: boolean;
  full_width_mode?: (keyof DataTableConfig)[];
  date_format?: string | null;
  time_format?: 'HH:mm' | 'hh:mm aa' | null;
  page_size?: number;
}

export interface UserGroup {
  id: number;
  group: string;
  permissions: Permission[];
}

export type SavedSearches = Search[];

export interface Permission {
  codename: string;
  id: number;
  name: string;
}

export interface AppVersion {
  oms_core: {
    version: string;
    hash: string;
  };
  polaris: {
    version: string;
    hash: string | null;
  };
}

export interface SiteObject {
  id: number;
  domain: string;
  name: string;
  splash_content: string;
  splash_content_type: string;
  updated_at: string;
  updated_by: number;
}

// aka PendingAsset
export type ValidationDataError = {
  md_attribute: string;
  message: string;
  api_field: string;
  title_level: string;
  supplemental_title_level: string | null;
  locale: string | null;
};
type ValidationDataUIKey = {
  label: string;
  display: boolean;
};
export type ValidationDataUIAttributes = {
  [K in keyof ValidationDataError]: ValidationDataUIKey;
};
export type PmamImage = {
  pmam_id: string;
  language: string;
  file_name: string;
  resolution: string;
};
export type AssetInfo = {
  action_url?: string;
  content_type?: string;
  sub_type?: string;
  language?: string;
  pmam_images?: PmamImage[];
} & Record<string, any>;
export interface OrderJobAsset {
  id: number;
  hid: string;
  description: string;
  filename: string;
  full_path: string;
  jobs: OrderJob[];
  base_path: string;
  version: TitleEntity;
  s3_bucket: string;
  status: string;
  status_reason?: string;
  schema?: RJSFSchema;
  status_changed_at: string;
  assigned_group: UserGroup;
  assigned_user: UserProfile | null;
  assigned_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  requested_resolution: '4k' | 'fhd' | 'sd' | 'hd' | null;
  audio_language: string | null;
  subtitle_language: string | null;
  jobs__due_date: string | null;
  comments_count: number;
  unread_comments_count: number;
  ingest_record_id?: number;
  info?: AssetInfo;
  type?: string;
  sub_type?: string;
  ingest_record?: number;
  material_id?: string;
  validation_data?: {
    is_metadata_valid: boolean;
    errors: ValidationDataError[];
    warnings: ValidationDataError[];
    ui_attributes: ValidationDataUIAttributes;
  };
  component_types?: string[];
  component_type?: string;
  earliest_job_due_date?: string;
  title?: TitleEntity;
}

export interface TableColumn extends ColumnConfig<any, any> {
  id: string;
  order: number;
  hidden: boolean;
}

export interface TimelineStage {
  name: string;
  status: 'not_started' | 'in_progress' | 'failed' | 'complete';
  status_reason: string;
}

export interface TimelineAction {
  action: string;
  display_name: string;
  status: 'not_started' | 'in_progress' | 'failed' | 'complete';
  status_reason: string;
}

export type IngestFileTitleSerialized = Pick<
  TitleEntity,
  'id' | 'name' | 'entity_type' | 'lineage_meta' | 'external_id' | 'alpha_id' | 'sequence_order'
> & {
  children: IngestFileTitleSerialized[];
};

export interface IngestFile {
  actions: TimelineAction[];
  alpha_id?: string;
  auto_ingest: boolean;
  caid?: string;
  component_audio_config?: string | null;
  component_deliverable_sub_type: string | null;
  component_deliverable_type: string | null;
  created_at: string;
  file_path: string;
  file_size: number;
  file_type: AssetType;
  filename: string;
  hid?: string;
  history_date?: string;
  id: number;
  ingest_schema: RJSFSchema;
  language_code: string;
  manifest?: any;
  media_info: MediaInfo;
  media_url?: string;
  pending_asset_id?: number;
  schema: RJSFSchema;
  schema_input?: {
    [key: string]: any;
  };
  schema_inputs?: {
    [key: string]: any;
  };
  src_bucket: string;
  stage_reason: string;
  stage: string;
  status: string;
  stages: TimelineStage[];
  status_reason: string;
  thumbnail_url?: string;
  title: number;
  title_serialized?: IngestFileTitleSerialized;
  tracking_id: string;
  unread_comments_count?: number;
  updated_at: string;
  version?: IngestFileTitleSerialized;
  version_id?: number | string;
  version_obj?: TitleEntity;
  version_serialized?: TitleEntity;
  due_date?: string;
  tech_metadata: TechMetadata;

  // client-specific properties
  upload_file_origin_id?: string;
}

export type TechMetadataFieldValue = string | number | boolean | null;

export interface TechMetadataMediaInfoField {
  field: string;
  label: string;
  value: TechMetadataFieldValue;
  compare_view_only: boolean;
}

export interface TechMetadataSection<T> {
  name: string;
  fields: T[];
}

export interface TechMetadataSpecificationField {
  field: string;
  label: string;
  expected: TechMetadataFieldValue;
  validated: boolean;
  media_info_field: string | null;
}

export interface TechMetadata {
  media_info: TechMetadataSection<TechMetadataMediaInfoField>[];
  specifications: TechMetadataSection<TechMetadataSpecificationField>[];
  failed_validation: string[][];
}

type MediaInfo = {
  [key: string]: any;
};

export interface TransferQueueItem {
  created: string;
  created_by: UserProfile;
  deleted: string | null;
  description: string;
  finished: string | null;
  id: number;
  is_deleted: boolean;
  progress: number;
  started: string | null;
  status: string;
  task_error_msg: string | null;
  updated: string;
  updated_by: UserProfile;
  [key: string]: any; // will eventually be updated & changed
}

export type CustomerProfileResponse = PolarisApiResponse<Profiles>;

type CustomerProfileWorkflowType = 'version_based' | 'component_based' | 'bulk';

export interface CustomerProfileData {
  [key: string]: any;
  workflow?: string;
  workflow_type?: CustomerProfileWorkflowType;
  audio_tracks?: string[];
  subtitle_tracks?: string[];
  media_resolution?: MediaResolutions;
  template_inputs?: Record<string, any>;
}

export interface CustomerProfile {
  category_1: string;
  category_2: string;
  created_at: string;
  created_by: UserProfile;
  customer_name: string;
  data: CustomerProfileData;
  deleted_at: string | null;
  id: number;
  name: string;
  label: string;
  schema: RJSFSchema;
  slug: string;
  updated_at: string;
  updated_by: UserProfile;
  workflow_type?: CustomerProfileWorkflowType;
}

export interface CustomerAssociations {
  name: string;
}

export interface IngestManagementFileHistoryResponse {
  success: boolean;
  message: string;
  data: IngestFileHistory[];
  pagination: Pagination;
}

export interface IngestFileHistory {
  history_date: string;
  changes: IngestFileHistoryChanges;
  history_user: Pick<UserProfile, 'color' | 'first_name' | 'last_name' | 'email' | 'id'>;
}

export interface Change {
  new: any;
  old: any;
}
export interface IngestFileHistoryChanges {
  status?: Change;
  status_reason?: Change;
  file_origin?: Change;
  file_path?: Change;
  file_type?: Change;
  filename?: Change;
  id?: Change;
  src_bucket?: Change;
  tracking_id?: Change;
  resolution_notes?: Change;
  assigned_user?: Change;
  priority?: Change;
  hid?: Change;
  source_file_path?: Change;
  data?: Change;
}

export interface Note {
  id: number;
  message: string;
  created_by: UserProfile & { color: any };
  created_at: string;
}

interface ReportFileInfo {
  file_size: number;
  file_type: string;
  file_name: string;
}

export type DailyReport = {
  created_at: string;
  customer: null | UserProfile;
  file_info: ReportFileInfo;
  id: string;
  name: string;
  updated_at: string;
  created_by?: UserProfile;
};

export type DailyReportResponse = {
  data: DailyReport[];
  pagination: Pagination;
  message: string;
  success: boolean;
};

export type GraphDirectoryItem = {
  id: string;
  label: string;
  fullRow?: boolean;
  selfManagedEditableView?: boolean;
  render: () => React.ReactNode;
};

export enum MediaResolutions {
  ANY = 'ANY',
  '4k' = '4k',
  UHD = 'UHD',
  FHD = 'FHD',
  HD = 'HD',
  SD = 'SD'
}

export type MediaResolution = {
  value: MediaResolutions;
  name: MediaResolutions;
  resolution: number[];
};

export interface IngestManagementState {
  selectedTitles: SelectedTitles | SelectedTitlesV2;
  recentTitles: SelectedTitles[] | SelectedTitlesV2[];
  selectedRecentTitles: SelectedTitles | SelectedTitlesV2 | null;
  registerAgainst: string | null;
}

export interface Exception {
  id: number;
  hid: string;
  created_at: string;
  updated_at: string;
  customer: {
    id: number;
    name: string;
  };
  severity: string;
  type: string;
  subtype: string;
  reference_id: string | null;
  reference_type: string;
  references: ExceptionReference[];
  status: string;
  priority: number;
  name: string;
  description: string;
  is_retryable: boolean;
  assigned_user: UserProfile | null;
  assigned_at: string | null;
  stack_trace?: string;
  status_reason?: string;
  input_schema?: RJSFSchema;
  actions?: ExceptionAction[];
  error_body?: string;
}

export enum ExceptionReferenceTypes {
  Job = 'job',
  Order = 'order',
  Asset = 'asset',
  Task = 'task',
  Ingest = 'ingest'
}

export type ExceptionReference = {
  type: ExceptionReferenceTypes;
  id: string;
  hid?: string; // old exceptions might not have this field
  is_primary: boolean;
};

export enum ExceptionLinkActionNames {
  GoToJob = 'go_to_job',
  GoToOrder = 'go_to_order',
  GoToAsset = 'go_to_asset',
  GoToTask = 'go_to_task',
  GoToIngest = 'go_to_ingest'
}

export interface ExceptionLinkAction {
  type: 'link';
  name: ExceptionLinkActionNames;
  label: string;
  detail: { url: string };
}

export enum ApiActionMethods {
  Get = 'GET',
  Post = 'POST'
}

export enum ExceptionApiActionNames {
  CancelJob = 'cancel_job',
  RestartJob = 'restart_job',
  RetryRallyJob = 'retry_rally_job'
}

export interface ExceptionApiAction {
  type: 'api';
  name: ExceptionApiActionNames;
  label: string;
  detail: {
    url: string;
    method: ApiActionMethods;
    body: Record<string, unknown>;
    body_schema: RJSFSchema | null;
  };
}

export type ExceptionAction = ExceptionLinkAction | ExceptionApiAction;

export type Availold = {
  hid: string;
  title: {
    name: string;
    name_supplemental: string | null;
    title_client_id: string;
    title_hid: string;
    title_id: number;
    title_type: string;
    title_version_type: string;
  };
  status: string;

  title_entity_id: number;
  title_entity_external_id: string;
};

export enum ElementInventoryStatus {
  // TODO: not full and possibly not entirely correct list. Should be replaced with correct list when we know it.
  Available = 'available',
  Pending = 'pending',
  InProgress = 'in_progress',
  Unavailable = 'unavailable',
  Error = 'error',
  Missing = 'missing'
}

export type ElementType = 'video' | 'audio' | 'timedtext' | 'metadata' | 'subtitle' | 'caption' | 'other' | 'image' | 'artwork';

export type Element = {
  element: {
    element_type: ElementType;
    audio_config?: string;
    element_subtype: string;
    language: string;
    title_id: number;
    video_resolution_type?: string;
  };
  element_status: string;
  inventory: null;
  inventory_status: ElementInventoryStatus;
  element_hash: string;
};

export type FocusAvail = {
  allowed_languages: string[];
  availed_av_language: string;
  availed_platform: string;
  created_at: string;
  customer_id: number;
  deleted_at: string | null;
  elements: Element[];
  hid: string;
  id: number;
  license: string;
  license_supplemental: string | null;
  territories: string[];
  territory: string;
  term_end: string;
  term_start: string;
  updated_at: string;
  version: {
    availed_type: string;
    client_id: string;
    client_ids_supplemental: {
      rightsline_id: string;
      alpha_id: string;
    };
    hid: string;
    id: number;
    title_name: string;
    title_name_supplemental: string;
    title_type: string;
    season_sequence_order: number | null;
    episode_sequence_order: number | null;
    title_thumbnail_link: string | null;
  };
  video_resolution_type: string;
};

type FocusSchedulingBase = {
  audio_languages: string[];
  created_at: string;
  status: string;
  timed_text_languages: string[];
  updated_at: string;
  updated_by: UserProfile | null;
  window_end: string;
  window_start: string;
};

type FocusSchedulingCustomer = {
  id: number;
  name: string;
};

type FocusSchedulingVersion = {
  alt_external_id: string;
  id: number;
  name: string;
  parent_name: string;
};

type FocusVersionSchedulingLineageEntity = {
  alt_external_id: string;
  id: number;
  name: string;
  rightsline_id: number;
  sequence_order: number;
};

type FocusVersionSchedulingLineageMeta = {
  brand: FocusVersionSchedulingLineageEntity | null;
  brand_name: string | null;
  brand_sequence_order: number | null;
  episode: FocusVersionSchedulingLineageEntity | null;
  episode_name: string | null;
  episode_sequence_order: number | null;
  movie: FocusVersionSchedulingLineageEntity | null;
  movie_name: string | null;
  ova: FocusVersionSchedulingLineageEntity | null;
  ova_name: string | null;
  parent_entity_type: string;
  season: FocusVersionSchedulingLineageEntity | null;
  season_name: string | null;
  season_sequence_order: number | null;
  series: FocusVersionSchedulingLineageEntity | null;
  series_name: string | null;
  series_sequence_order: number | null;
  version: FocusVersionSchedulingLineageEntity;
};

type FocusVersionSchedulingTitle = {
  availed_type: string;
  client_id: string;
  client_ids_supplemental: {
    alpha_id: string;
    rightsline_id: string;
  };
  episode_sequence_order: number | null;
  hid: string;
  id: number;
  season_sequence_order: number | null;
  title_name: string;
  title_name_supplemental: string;
  title_thumbnail_link: string | null;
  title_type: string;
};

type FocusEntitySchedulingVersion = FocusSchedulingVersion & {
  entity_type: 'version';
  hid: string;
  lineage_meta: FocusVersionSchedulingLineageMeta;
};

export type FocusScheduling = FocusSchedulingBase & {
  customer: FocusSchedulingCustomer;
  id: number;
  license: string;
  platform: string;
  prev_scheduling_id: number | null;
  primary_av_lang: 'ja-jp';
  territory: string[];
  version: FocusSchedulingVersion;
  version_scheduling_id: FocusVersionScheduling['id'];
};

export type FocusVersionScheduling = FocusSchedulingBase & {
  allowed_languages: string[];
  availed_platform: string;
  availed_platforms: string[];
  customer: FocusSchedulingCustomer;
  elements: Element[];
  elements_completed: number;
  elements_total: number;
  hid: string;
  id: number;
  license: string;
  license_type: string;
  license_types: string[];
  platform: string;
  primary_av_lang: 'ja-jp';
  schedule_id: number;
  scheduling_ids: number[];
  term_end: string;
  term_start: string;
  territories: string[];
  territory: string[];
  thread: null;
  title: FocusVersionSchedulingTitle;
  version: FocusEntitySchedulingVersion;
  window_term: string;
};

export type FocusSchedulingStats = {
  day_start: string;
  day_end: string;
  stats: { total_count: number; status_ready_count: number; status_pending_count: number; status_error_count: number };
};

export type FocusCalendar = any;

export type Schedule = any;

export type TerritoryConstant = {
  label: string;
  value: string;
};

export type FocusReport = {
  id: number;
  name: string;
  file_info: ReportFileInfo;
  tag: string;
  updated_at: string;
  created_at: string;
  created_by: UserProfile;
};

export type SchedulingPostData = {
  version_id: number;
  customer_id: number | string;
  primary_av_lang: 'ja-jp';
  platform: string;
  license: string;
  window_start: string;
  window_end: string;
  territory: string[];
  audio_languages: string[];
  timed_text_languages: string[];
};

export type ScheduleAvailType = 'audio' | 'timedtext' | 'territories';
export type ScheduleAvailRights = Record<ScheduleAvailType, string[]>;
